<template>
  <div class="edition-table">
    <div v-if="loading" class="mb-1 w-100 text-center">
      <Skeleton width="659px" height="157px" />
    </div>

    <div v-else>
      <table>
        <thead>
          <th class="edition-table__room">Quartos</th>

          <!-- HÓSPEDES -->
          <th
            v-for="(pax, i) in paxOptionsStrings"
            :key="`th-pax-${i}`"
            class="edition-table__pax"
          >
            {{ pax }}
          </th>
        </thead>

        <tbody>
          <!-- LINHA -->
          <tr
            v-for="(room, i) in rooms"
            :key="`room-${room.codigoQuarto || i}`"
          >
            <!-- QUARTOS -->
            <td class="td__padd">{{ room.quarto }}</td>
            <td
              v-for="(pax, i) in getPossiblePaxColumns(room)"
              :key="`th-pax-${i}`"
              :class="{
                empty: !pax
              }"
              class="td__padd"
            >
              {{ pax?.quantidadePontos }} <span v-if="pax">pts</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
/** ======================================================= */
/** Componente da tabela de visualização da tarifa absoluta */
/** ======================================================= */
import { mapGetters } from 'vuex'
import Skeleton from '@/components/_base/elements/Skeleton'

export default {
  name: 'DayInfoTable',
  components: {
    Skeleton
  },
  props: {
    /** Configuração dos quartos */
    rooms: {
      type: Array,
      default: () => []
    },

    /** Pegar data */
    date: {
      type: [String, Date],
      default: null
    },

    /** Mostra skeleton loader */
    loading: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    /**
     * Retorna a possibilidade máxima de adultos e crianças
     * entre todos os quartos
     */
    paxOptions() {
      return (
        (this.rooms || [])
          .flatMap((item) => item.pontosPax)
          /** Filtra apenas valores que não são repetidos */
          .filter(
            (item, index, arr) =>
              index ===
              arr.findIndex(
                (elm) =>
                  elm.quantidadePax === item.quantidadePax &&
                  elm.tipoPax === item.tipoPax
              )
          )
          /** Ordena em ordem crescente adultos seguido de crianças */
          .sort((a, b) => {
            if (a.tipoPax > b.tipoPax) return -1
            if (b.tipoPax > a.tipoPax) return 1

            return 0
          })
      )
    },
    /** Retorna os textos dos cabeçalhos */
    paxOptionsStrings() {
      return this.paxOptions.map((item) => {
        if (item.tipoPax === 8)
          return `${this.$options.filters.pluralizer(
            item.quantidadePax,
            'Criança | Crianças'
          )}`

        return `${item.quantidadePax} Pax`
      })
    },
    ...mapGetters('hotel', ['getCurrency', 'getCurrencySymbol'])
  },
  methods: {
    /**
     * Retorna um array com os campos de hóspedes e vazios de cada quarto
     *
     * @param {Object} room - Objeto do quarto a ser pesquisado
     */
    getPossiblePaxColumns(room) {
      /** Cria um novo array preenchido com null */
      const cols = new Array(this.paxOptions.length).fill(null)

      room.pontosPax.forEach((roomPax) => {
        const idx = this.paxOptions.findIndex(
          (paxOptionsItem) =>
            paxOptionsItem.tipoPax === roomPax.tipoPax &&
            paxOptionsItem.quantidadePax === roomPax.quantidadePax
        )

        if (idx > -1) {
          cols[idx] = roomPax
        }
      })

      this.loading = false
      return cols
    }
  }
}
</script>

<style lang="scss" scoped>
$cell-padding: 24px 16px;

.edition-table {
  max-width: 1236px;
  overflow-x: auto;

  &__room {
    width: 77px;
    min-width: 77px;
  }

  &__value {
    width: 77px;
    min-width: 77px;
  }

  &__pax {
    width: 77px;
    min-width: 77px;
  }

  table {
    th {
      padding: $cell-padding;
    }

    th,
    td {
      text-align: center;
      vertical-align: middle;
      border-top: 1px solid $gray-border;
      border-bottom: 1px solid $gray-border;
      font-size: font-size('size-medium');
      white-space: nowrap;

      & + & {
        border-left: none;
      }

      &.empty {
        background-color: $gray-lightest;
      }
    }

    th:nth-child(1),
    th:nth-child(2),
    td:nth-child(1),
    td:nth-child(2) {
      border: 1px solid $gray-border;
    }

    th:last-child,
    td:last-child {
      border-right: 1px solid $gray-border;
    }

    th {
      font-weight: font-weight('weight-medium');
    }

    .td {
      padding: 0;
      min-width: 1px;
      border-right: none;

      &__padd {
        padding: $cell-padding;
        ::v-deep .input-checkbox {
          margin: 0px auto !important;
          display: table;
        }
      }
    }

    .rest-td {
      .td {
        &:first-child {
          border-right: 1px solid $gray-border;
        }

        &:last-child {
          ::v-deep {
            .content-wrapper {
              padding: {
                left: 10px;
                right: 10px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
